import React, { useState, useEffect } from 'react';
import {
  Calendar,
  DollarSign,
  FileText,
  Heart,
  AlertTriangle,
  Users,
  Activity,
  Settings,
  Plus,
  Check,
  X,
  Edit3,
  Phone,
  Home,
  TrendingUp,
  BarChart3,
  Target,
  Zap,
  CalendarOff
} from 'lucide-react';
import { useSubscriptionOverlay } from './hooks/useSubscriptionOverlay';

// Type definitions
type ScheduleData = {
  [key: string]: {
    [key: string]: boolean;
  };
};

type DocumentData = {
  name: string;
  completed: boolean;
  updated: boolean;
  [key: string]: boolean | string; // Allow indexing with string keys
};

// Add proper TypeScript interfaces
interface FamilyMember {
  name: string;
  relationship: string;
  age: number;
  care_percentage: number;
}

interface RecommendationItem {
  recommendation: string;
  resource?: {
    name: string;
    address: string;
    phone: string;
    website: string;
    description: string;
  };
}

interface PlanData {
  assistant_title: string;
  family_snapshot: {
    family_members: FamilyMember[];
    analysis?: RecommendationItem[];
  };
  financials: {
    "Gross Income": number;
    "Net Income": number;
    "Net Savings": number;
    expense: Array<{
      category: string;
      amount: number;
    }>;
    analysis?: RecommendationItem[];
  };
  health_monitoring: Array<{
    burnout_rate: number;
    resource_programs: Array<{
      title: string;
      url: string;
    }>;
    analysis?: RecommendationItem[];
  }>;
}

const CaregivingDashboard = () => {
  // Custom hook for subscription overlay
  const { SubscriptionOverlay } = useSubscriptionOverlay();

  const [familyData, setFamilyData] = useState({
    members: [
      { name: 'Richard', age: 38, role: 'Son/Caregiver', careLevel: 30 },
      { name: 'Susan', age: 55, role: 'Spouse', careLevel: 0 },
      { name: 'Patricia', age: 82, role: 'Mother', careLevel: 60 }
    ],
    alerts: [
      'No advance directive for Patricia',
      'Medicare annual wellness visit due'
    ]
  });

  const [financialData, setFinancialData] = useState({
    budgetBreakdown: {
      careCosts: 45,
      childrenExpenses: 30,
      other: 25
    },
    retirementVsCollege: [
      { month: 'Jan', retirement: 2500, college: 1200 },
      { month: 'Feb', retirement: 2600, college: 1300 },
      { month: 'Mar', retirement: 2750, college: 1250 },
      { month: 'Apr', retirement: 2800, college: 1400 },
      { month: 'May', retirement: 2900, college: 1450 },
      { month: 'Jun', retirement: 3000, college: 1500 }
    ]
  });

  const [documents, setDocuments] = useState<DocumentData[]>([
    { name: 'Will', completed: true, updated: true },
    { name: 'Power of Attorney', completed: true, updated: true },
    { name: 'HIPAA Release', completed: true, updated: true }
  ]);

  const [healthData, setHealthData] = useState({
    medicationCompliance: 90,
    burnoutLevel: 30
  });

  const [schedule, setSchedule] = useState<ScheduleData>({
    'Susan': { Mo: false, Tu: false, We: true, Th: false, Fr: false, Sa: false, Su: false },
    'Richard': { Mo: true, Tu: false, We: false, Th: false, Fr: false, Sa: false, Su: false },
    'Patricia': { Mo: false, Tu: false, We: true, Th: false, Fr: false, Sa: false, Su: false }
  });

  // const [resources] = useState([
  //   { name: 'Area Agency on Aging', link: 'https://app.rejara.com/' },
  //   { name: 'Medicaid', link: 'https://app.rejara.com/' },
  //   { name: 'Respite Care', link: 'https://app.rejara.com/' }
  // ]);

  const [activeTab, setActiveTab] = useState('overview');
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', age: '', role: '' });

  // Scenario Planning Data
  const [scenarios, setScenarios] = useState([
    {
      id: 1,
      name: 'Current Trajectory',
      type: 'baseline',
      description: 'Continuing current care level and spending',
      parameters: {
        careLevel: 'moderate',
        healthDecline: 'gradual',
        livingArrangement: 'aging_in_place',
        careHours: 15,
        monthlyCost: 2250,
        timeHorizon: '5_years'
      },
      outcomes: {
        totalCost: 135000,
        caregiverBurnout: 35,
        qualityOfLife: 75,
        financialStrain: 30
      }
    },
    {
      id: 2,
      name: 'Health Crisis',
      type: 'crisis',
      description: 'Sudden health decline requiring intensive care',
      parameters: {
        careLevel: 'intensive',
        healthDecline: 'rapid',
        livingArrangement: 'assisted_living',
        careHours: 40,
        monthlyCost: 5500,
        timeHorizon: '3_years'
      },
      outcomes: {
        totalCost: 198000,
        caregiverBurnout: 75,
        qualityOfLife: 50,
        financialStrain: 80
      }
    },
    {
      id: 3,
      name: 'Proactive Planning',
      type: 'optimized',
      description: 'Early intervention with professional care support',
      parameters: {
        careLevel: 'supported',
        healthDecline: 'slow',
        livingArrangement: 'home_with_services',
        careHours: 25,
        monthlyCost: 3200,
        timeHorizon: '7_years'
      },
      outcomes: {
        totalCost: 268800,
        caregiverBurnout: 20,
        qualityOfLife: 85,
        financialStrain: 45
      }
    }
  ]);

  const [selectedScenario, setSelectedScenario] = useState(scenarios[0]);
  const [showScenarioBuilder, setShowScenarioBuilder] = useState(false);

  const scenarioParameters = {
    careLevel: {
      label: 'Care Level Required',
      options: [
        { value: 'minimal', label: 'Minimal (Check-ins, errands)', cost: 800 },
        { value: 'moderate', label: 'Moderate (Daily assistance)', cost: 2250 },
        { value: 'supported', label: 'Supported (Professional help)', cost: 3200 },
        { value: 'intensive', label: 'Intensive (24/7 monitoring)', cost: 5500 },
        { value: 'memory_care', label: 'Memory Care Specialized', cost: 6500 }
      ]
    },
    healthDecline: {
      label: 'Rate of Health Decline',
      options: [
        { value: 'stable', label: 'Stable (No decline)', multiplier: 0.8 },
        { value: 'slow', label: 'Slow (Gradual over 7+ years)', multiplier: 1.0 },
        { value: 'gradual', label: 'Gradual (Steady over 3-5 years)', multiplier: 1.2 },
        { value: 'rapid', label: 'Rapid (Significant within 2 years)', multiplier: 1.6 }
      ]
    },
    livingArrangement: {
      label: 'Living Arrangement',
      options: [
        { value: 'aging_in_place', label: 'Aging in Place (Current home)', cost: 1000 },
        { value: 'home_with_services', label: 'Home with Services', cost: 2000 },
        { value: 'adult_child_home', label: 'Adult Child\'s Home', cost: 1500 },
        { value: 'assisted_living', label: 'Assisted Living Facility', cost: 4500 },
        { value: 'memory_care_facility', label: 'Memory Care Facility', cost: 6000 },
        { value: 'nursing_home', label: 'Nursing Home', cost: 8000 }
      ]
    },
    timeHorizon: {
      label: 'Planning Time Horizon',
      options: [
        { value: '2_years', label: '2 Years', years: 2 },
        { value: '3_years', label: '3 Years', years: 3 },
        { value: '5_years', label: '5 Years', years: 5 },
        { value: '7_years', label: '7 Years', years: 7 },
        { value: '10_years', label: '10 Years', years: 10 }
      ]
    },
    geography: {
      label: 'Geographic Location',
      options: [
        { value: 'rural', label: 'Rural Area', costMultiplier: 0.7 },
        { value: 'suburban', label: 'Suburban Area', costMultiplier: 1.0 },
        { value: 'urban', label: 'Urban Area', costMultiplier: 1.3 },
        { value: 'high_cost_urban', label: 'High-Cost Urban (NYC, SF)', costMultiplier: 1.8 }
      ]
    },
    familySupport: {
      label: 'Family Support Level',
      options: [
        { value: 'minimal', label: 'Minimal (Occasional help)', multiplier: 1.4 },
        { value: 'moderate', label: 'Moderate (Regular assistance)', multiplier: 1.0 },
        { value: 'high', label: 'High (Daily family involvement)', multiplier: 0.7 },
        { value: 'primary', label: 'Primary Caregiver Available', multiplier: 0.5 }
      ]
    }
  };

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Update the state with proper typing
  const [planData, setPlanData] = useState<PlanData>({
    assistant_title: '',
    family_snapshot: {
      family_members: [],
      analysis: []
    },
    financials: {
      "Gross Income": 0,
      "Net Income": 0,
      "Net Savings": 0,
      expense: [],
      analysis: []
    },
    health_monitoring: []
  });

  // Add useEffect for API call
  useEffect(() => {
    let isMounted = true;
    let abortController = new AbortController();

    const fetchPlanData = async () => {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const userGuid = urlParams.get('user');
      const assistantGuid = urlParams.get('assistant');

      // Check if required parameters are present
      if (!userGuid || !assistantGuid) {
        console.warn('Missing required URL parameters: user and/or assistant');
        setError('Missing required URL parameters');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetch(process.env.REACT_APP_Plan_API || 'http://127.0.0.1:8000/plan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_APIKEY  || ''
          },
          body: JSON.stringify({
            user_guid: userGuid,
            assistant_guid: assistantGuid,
          }),
          signal: abortController.signal
        });

        if (response.ok && isMounted) {
          const data = await response.json();
          if (data.plan) {
            setPlanData(data.plan);
            setError(null);
          } else {
            setError('No plan data received from server');
          }
        } else if (isMounted) {
          const errorMsg = `API request failed: ${response.status} ${response.statusText}`;
          setError(errorMsg);
          console.error('API Error:', errorMsg);
        }
      } catch (error) {
        if (isMounted && (error as Error).name !== 'AbortError') {
          const errorMsg = `Error fetching plan data: ${error}`;
          setError(errorMsg);
          console.error('❌ Fetch Error:', errorMsg);
        } else if ((error as Error).name === 'AbortError') {
          console.log('🚫 API call aborted');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPlanData();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []); // Empty dependency array

  const addFamilyMember = () => {
    if (newMember.name && newMember.age && newMember.role) {
      setFamilyData(prev => ({
        ...prev,
        members: [...prev.members, { ...newMember, age: parseInt(newMember.age), careLevel: 0 }]
      }));
      setNewMember({ name: '', age: '', role: '' });
      setShowAddMember(false);
    }
  };

  const toggleSchedule = (person: string, day: string) => {
    setSchedule(prev => ({
      ...prev,
      [person]: {
        ...prev[person],
        [day]: !prev[person][day]
      }
    }));
  };

  const updateDocument = (index: number, field: string) => {
    setDocuments(prev => prev.map((doc, i) =>
      i === index ? { ...doc, [field]: !doc[field] } : doc
    ));
  };

  interface TabButtonProps {
    id: string;
    label: string;
    icon: React.ComponentType<{ size: number }>;
    active: boolean;
    onClick: (id: string) => void;
  }

  const TabButton = ({ id, label, icon: Icon, active, onClick }: TabButtonProps) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${active
        ? 'bg-violet-900 text-white'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
    >
      <Icon size={18} />
      {label}
    </button>
  );

  interface ProgressBarProps {
    value: number;
    color?: string;
  }

  const ProgressBar = ({ value, color = 'blue' }: ProgressBarProps) => (
    <div className="w-full bg-gray-200 rounded-full h-3">
      <div
        className={color === 'blue' ? `h-3 rounded-full bg-violet-900` : `h-3 rounded-full bg-red-500`}
        style={{ width: `${value}%` }}
      />
    </div>
  );

  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Family Snapshot */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Family Snapshot</h3>
          {/* <button
            onClick={() => setShowAddMember(true)}
            className="p-2 bg-violet-900 rounded-lg hover:bg-blue-900 transition-colors"
          >
            <Plus size={18} className="text-white" />
          </button> */}
        </div>

        <div className="space-y-4">
          {isLoading ? (
            // Loading skeleton for family members
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-200 rounded-lg">
                <div className="flex-1">
                  <div className="animate-pulse">
                    <div className="h-5 bg-gray-300 rounded w-24 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-32"></div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-300 rounded w-12 mb-1"></div>
                    <div className="h-3 bg-gray-300 rounded w-12"></div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Actual family data
            planData.family_snapshot.family_members.map((member, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-stone-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">{member.name}</div>
                  {member.name !== member.relationship && (
                    <div className="text-sm text-gray-600">
                      {member.age > 0 ? `(${member.age}) - ` : ''}{member.relationship}
                    </div>
                  )}
                  {member.name === member.relationship && member.age > 0 && (
                    <div className="text-sm text-gray-600">({member.age})</div>
                  )}
                </div>
                {member.care_percentage > 0 && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-violet-900">{member.care_percentage}%</div>
                    <div className="text-xs text-gray-500">Care</div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Family Recommendations */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-700 mb-3">Recommendations</h4>
          {isLoading ? (
            // Loading skeleton for recommendations
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-start gap-2 animate-pulse">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 rounded w-full mb-1"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              {planData.family_snapshot.analysis && Array.isArray(planData.family_snapshot.analysis) && planData.family_snapshot.analysis.length > 0 ? (
                <div className="p-4 bg-stone-100 border border-stone-300 rounded-lg">
                  <div className="space-y-2">
                    {planData.family_snapshot.analysis.map((item: RecommendationItem, index: number) => (
                      <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 bg-violet-900 rounded-full mt-2 flex-shrink-0" />
                        <div className="leading-relaxed flex-1">
                          <div className="mb-2">{item.recommendation}</div>
                          {item.resource && item.resource.name && (
                            <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                              <div className="text-xs">
                                <div className="font-medium text-gray-800">
                                  {item.resource.website ? (
                                    <a 
                                      href={item.resource.website} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-violet-900 hover:text-violet-700 underline"
                                    >
                                      {item.resource.name}
                                    </a>
                                  ) : (
                                    item.resource.name
                                  )}
                                </div>
                                {item.resource.address && (
                                  <div className="text-gray-600 mt-1">{item.resource.address}</div>
                                )}
                                {item.resource.phone && (
                                  <div className="text-gray-600 mt-1">{item.resource.phone}</div>
                                )}
                                {item.resource.description && (
                                  <div className="text-gray-600 mt-1">{item.resource.description}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">No recommendations available at this time.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Financial Overview */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Overview</h3>

        {(() => {
          // Check if there are any income or expenses available
          const hasIncome = planData.financials["Gross Income"] > 0 || planData.financials["Net Income"] > 0;
          const hasExpenses = (planData.financials.expense || []).some(expense => {
            if (expense.category && expense.amount !== undefined) {
              return expense.amount > 0;
            } else {
              const category = Object.keys(expense)[0];
              const amount = expense[category as keyof typeof expense] as number;
              return amount > 0;
            }
          });
          
          // Only show budget breakdown if there's income or expenses
          if (!hasIncome && !hasExpenses) {
            return null;
          }
          
          return (
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3">Budget Breakdown</h4>
              {isLoading ? (
                // Loading skeleton for financial bar
                <div className="animate-pulse">
                  <div className="flex rounded-lg overflow-hidden h-4 mb-2">
                    <div className="bg-gray-200 w-1/3"></div>
                    <div className="bg-gray-200 w-1/3"></div>
                    <div className="bg-gray-200 w-1/3"></div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-gray-300 rounded"></div>
                      <div className="h-4 bg-gray-300 rounded w-16"></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-gray-300 rounded"></div>
                      <div className="h-4 bg-gray-300 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex rounded-lg overflow-hidden h-4 mb-2">
                    {/* Dynamic financial bars based on available data */}
                    {(() => {
                      // Get only expenses and net savings for the bars
                      const barData = [
                        ...(planData.financials.expense || [])
                          .map((expense, index) => {
                            // Handle the correct data structure: { category: "name", amount: 123 }
                            if (expense.category && expense.amount !== undefined) {
                              return { key: expense.category, value: expense.amount, type: 'expense' };
                            } else {
                              // Fallback for old format: { "name": 123 }
                              const category = Object.keys(expense)[0];
                              const amount = expense[category as keyof typeof expense] as number;
                              return { key: category, value: amount, type: 'expense' };
                            }
                          })
                          .filter(item => item.value > 0),
                        // Add Net Savings if it exists and is greater than 0
                        ...(planData.financials["Net Savings"] > 0 ? [{ key: 'Net Savings', value: planData.financials["Net Savings"], type: 'savings' }] : [])
                      ];

                      const total = barData.reduce((sum, item) => sum + item.value, 0);

                      return barData.map((item, index) => {
                        if (item.value === 0) {
                          return null;
                        }
                        // Define colors based on type and value
                        let color;
                        if (item.key === 'Net Savings') {
                          // Green for positive savings, orange/red for negative or zero
                          if (item.value > 0) {
                            color = 'bg-green-500';
                          } else if (item.value < 0) {
                            color = 'bg-red-500';
                          } else {
                            color = 'bg-orange-500';
                          }
                        } else {
                          // Shades of red for expenses
                          const redShades = ['bg-red-300', 'bg-red-400', 'bg-red-600', 'bg-red-700'];
                          color = redShades[index % redShades.length];
                        }

                        return (
                          <div
                            key={item.key}
                            className={color}
                            style={{ width: `${(item.value / total) * 100}%` }}
                          />
                        );
                      });
                    })()}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {(() => {
                      // Get only expenses and net savings for the legend
                      const legendData = [
                        ...(planData.financials.expense || [])
                          .map((expense, index) => {
                            // Handle the correct data structure: { category: "name", amount: 123 }
                            if (expense.category && expense.amount !== undefined) {
                              return { key: expense.category, value: expense.amount, type: 'expense' };
                            } else {
                              // Fallback for old format: { "name": 123 }
                              const category = Object.keys(expense)[0];
                              const amount = expense[category as keyof typeof expense] as number;
                              return { key: category, value: amount, type: 'expense' };
                            }
                          })
                          .filter(item => item.value > 0),
                        // Add Net Savings if it exists
                        { key: 'Net Savings', value: planData.financials["Net Savings"], type: 'savings' }
                      ];

                      return legendData.map((item, index) => {
                        // Define colors based on type and value
                        let color;
                        if (item.key === 'Net Savings') {
                          // Green for positive savings, orange/red for negative or zero
                          if (item.value > 0) {
                            color = 'bg-green-500';
                          } else if (item.value < 0) {
                            color = 'bg-red-500';
                          } else {
                            color = 'bg-orange-500';
                          }
                        } else {
                          // Shades of red for expenses
                          const redShades = ['bg-red-300', 'bg-red-400', 'bg-red-600', 'bg-red-700'];
                          color = redShades[index % redShades.length];
                        }

                        return (
                          <div key={item.key} className="flex items-center gap-1">
                            <div className={`w-3 h-3 ${color} rounded`} />
                            <span>{item.key}: ${item.value.toLocaleString()}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {(() => {
          // Check if there are any income or expenses available
          const hasIncome = planData.financials["Gross Income"] > 0 || planData.financials["Net Income"] > 0;
          const hasExpenses = (planData.financials.expense || []).some(expense => {
            if (expense.category && expense.amount !== undefined) {
              return expense.amount > 0;
            } else {
              const category = Object.keys(expense)[0];
              const amount = expense[category as keyof typeof expense] as number;
              return amount > 0;
            }
          });
          
          // Only show monthly money snapshot if there's income or expenses
          if (!hasIncome && !hasExpenses) {
            return null;
          }
          
          return (
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Monthly Money Snapshot</h4>
              {isLoading ? (
                // Loading skeleton for expenses - dynamic based on actual data length
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex justify-between items-center animate-pulse">
                      <div className="h-4 bg-gray-300 rounded w-20"></div>
                      <div className="h-4 bg-gray-300 rounded w-16"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="space-y-2 overflow-y-auto max-h-[300px]">
                    {(() => {
                      // Get all financial data including income, expenses, and savings
                      const allFinancialData = [
                        ...Object.entries(planData.financials)
                          .filter(([key, value]) => key !== 'expense' && typeof value === 'number' && value !== null && value !== undefined)
                          .map(([key, value]) => ({ key, value: value as number, type: 'income' })),
                        ...(planData.financials.expense || [])
                          .map((expense, index) => {
                            // Handle the correct data structure: { category: "name", amount: 123 }
                            if (expense.category && expense.amount !== undefined) {
                              return { key: expense.category, value: expense.amount, type: 'expense' };
                            } else {
                              // Fallback for old format: { "name": 123 }
                              const category = Object.keys(expense)[0];
                              const amount = expense[category as keyof typeof expense] as number;
                              return { key: category, value: amount, type: 'expense' };
                            }
                          })
                      ];

                      // Sort: Gross Income, Net Income, Expenses, Net Savings
                      const sortedData = allFinancialData.sort((a, b) => {
                        const order = { 'Gross Income': 1, 'Net Income': 2, 'Net Savings': 4 };
                        const aOrder = order[a.key as keyof typeof order] || 3;
                        const bOrder = order[b.key as keyof typeof order] || 3;
                        return aOrder - bOrder;
                      });

                      // Group expenses together
                      const incomeItems = sortedData.filter(item => item.type === 'income');
                      const expenseItems = sortedData.filter(item => item.type === 'expense');
                      const savingsItems = sortedData.filter(item => item.key === 'Net Savings');

                      return (
                        <>
                          {/* Income Items */}
                          {incomeItems.filter(item => item.key !== 'Net Savings').map((item, index) => {
                            let textColor = 'text-violet-900';
                            return (
                              <div key={index} className="flex justify-between text-sm font-semibold px-1">
                                <span>{item.key}</span>
                                <span className={textColor}>${item.value.toLocaleString()}</span>
                              </div>
                            );
                          })}

                          {/* Expenses Group with Border */}
                          {expenseItems.length > 0 && (
                            <div className="bg-stone-200 rounded-lg p-1 mt-3 mb-3">
                              <div className="text-sm font-semibold text-gray-600 mb-2">Expenses</div>
                              {expenseItems.map((item, index) => {
                                if (item.value === 0) {
                                  return null;
                                }
                                return (
                                <div key={index} className="flex justify-between text-sm font-semibold mb-1 last:mb-0">
                                  <span>{item.key}</span>
                                  <span className="text-red-600">${item.value.toLocaleString()}</span>
                                </div>
                              );
                              })}
                            </div>
                          )}

                          {/* Savings Items */}
                          {savingsItems.map((item, index) => {
                            let textColor;
                            if (item.value > 0) {
                              textColor = 'text-green-600';
                            } else if (item.value < 0) {
                              textColor = 'text-red-500';
                            } else {
                              textColor = 'text-orange-600';
                            }

                            return (
                              <div key={index} className="flex justify-between text-sm font-semibold px-1">
                                <span>{item.key}</span>
                                <span className={textColor}>${item.value.toLocaleString()}</span>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Financial Recommendations */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-700 mb-3">Recommendations</h4>
          {isLoading ? (
            // Loading skeleton for recommendations
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-start gap-2 animate-pulse">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 rounded w-full mb-1"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
                      <div>
            {planData.financials.analysis && Array.isArray(planData.financials.analysis) && planData.financials.analysis.length > 0 ? (
              <div className="p-4 bg-stone-100 border border-stone-300 rounded-lg">
                <div className="space-y-2">
                  {planData.financials.analysis.map((item: RecommendationItem, index: number) => (
                    <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <div className="w-2 h-2 bg-violet-900 rounded-full mt-2 flex-shrink-0" />
                      <div className="leading-relaxed flex-1">
                        <div className="mb-2">{item.recommendation}</div>
                        {item.resource && item.resource.name && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="text-xs">
                              <div className="font-medium text-gray-800">
                                {item.resource.website ? (
                                  <a 
                                    href={item.resource.website} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-violet-900 hover:text-violet-700 underline"
                                  >
                                    {item.resource.name}
                                  </a>
                                ) : (
                                  item.resource.name
                                )}
                              </div>
                              {item.resource.address && (
                                <div className="text-gray-600 mt-1">{item.resource.address}</div>
                              )}
                              {item.resource.phone && (
                                <div className="text-gray-600 mt-1">{item.resource.phone}</div>
                              )}
                              {item.resource.description && (
                                <div className="text-gray-600 mt-1">{item.resource.description}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">No recommendations available at this time.</div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Health Monitoring */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Health Monitoring</h3>

        <div className="space-y-4">
          {/* <div>
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-gray-700">Medication Compliance</span>
              <span className="text-lg font-bold text-green-600">{healthData.medicationCompliance}%</span>
            </div>
            <ProgressBar value={healthData.medicationCompliance} color="green" />
          </div> */}
          {isLoading ? (
            // Loading skeleton for health monitoring
            <div className="animate-pulse">
              <div className="flex justify-between items-center mb-2">
                <div className="h-5 bg-gray-300 rounded w-32"></div>
                <div className="h-6 bg-gray-300 rounded w-12"></div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                {/* <div className="bg-gray-300 h-3 rounded-full w-1/3"></div> */}
              </div>
            </div>
          ) : (
            planData.health_monitoring[0] && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">Burnout Check-in</span>
                  <span className="text-lg font-bold text-red-500">
                    {planData.health_monitoring[0].burnout_rate}%
                  </span>
                </div>
                <ProgressBar value={planData.health_monitoring[0].burnout_rate} color="red" />
              </div>
            )
          )}
        </div>

        <div className="mt-6">
          <h4 className="font-medium text-gray-700 mb-3">Resources & Programs</h4>
          {isLoading ? (
            // Loading skeleton for resources
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center gap-2 animate-pulse">
                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-40"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {planData.health_monitoring[0]?.resource_programs?.map((resource, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-violet-900">
                  <div className="w-2 h-2 bg-violet-900 rounded-full" />
                  <a href={resource.url} target="_blank" rel="noopener noreferrer" className="text-violet-900 hover:underline">{resource.title}</a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-700 mb-3">Recommendations</h4>
          {isLoading ? (
            // Loading skeleton for recommendations
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-start gap-2 animate-pulse">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 rounded w-full mb-1"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
                     ) : (
             <div>
               {planData.health_monitoring[0]?.analysis && Array.isArray(planData.health_monitoring[0].analysis) && planData.health_monitoring[0].analysis.length > 0 ? (
                 <div className="p-4 bg-stone-100 border border-stone-300 rounded-lg">
                   <div className="space-y-2">
                     {planData.health_monitoring[0].analysis.map((item: RecommendationItem, index: number) => (
                       <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                         <div className="w-2 h-2 bg-violet-900 rounded-full mt-2 flex-shrink-0" />
                         <div className="leading-relaxed flex-1">
                           <div className="mb-2">{item.recommendation}</div>
                           {item.resource && item.resource.name && (
                             <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                               <div className="text-xs">
                                 <div className="font-medium text-gray-800">
                                   {item.resource.website ? (
                                     <a 
                                       href={item.resource.website} 
                                       target="_blank" 
                                       rel="noopener noreferrer"
                                       className="text-violet-900 hover:text-violet-700 underline"
                                     >
                                       {item.resource.name}
                                     </a>
                                   ) : (
                                     item.resource.name
                                   )}
                                 </div>
                                 {item.resource.address && (
                                   <div className="text-gray-600 mt-1">{item.resource.address}</div>
                                 )}
                                 {item.resource.phone && (
                                   <div className="text-gray-600 mt-1">{item.resource.phone}</div>
                                 )}
                                 {item.resource.description && (
                                   <div className="text-gray-600 mt-1">{item.resource.description}</div>
                                 )}
                               </div>
                             </div>
                           )}
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               ) : (
                 <div className="text-sm text-gray-500 italic">No recommendations available at this time.</div>
               )}
             </div>
           )}
        </div>
      </div>
    </div>
  );

  const renderSchedule = () => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-6">Care Schedule</h3>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-700">Person</th>
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                <th key={day} className="text-center py-3 px-4 font-medium text-gray-700">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(schedule).map(([person, days]) => (
              <tr key={person} className="border-b border-gray-100">
                <td className="py-4 px-4 font-medium text-gray-800">{person}</td>
                {Object.entries(days).map(([day, isScheduled]) => (
                  <td key={day} className="text-center py-4 px-4">
                    <button
                      onClick={() => toggleSchedule(person, day)}
                      className={`w-8 h-8 rounded-lg border-2 transition-colors ${isScheduled
                        ? 'bg-violet-900 border-violet-900 text-white'
                        : 'border-gray-300 hover:border-blue-900'
                        }`}
                    >
                      {isScheduled && <Check size={16} className="text-white text-center mx-1.5" />}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-6">Document Completion</h3>

      <div className="space-y-4">
        {documents.map((doc, index) => (
          <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <button
                onClick={() => updateDocument(index, 'completed')}
                className={`w-6 h-6 rounded border-2 flex items-center justify-center ${doc.completed
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'border-gray-300'
                  }`}
              >
                {doc.completed && <Check size={14} />}
              </button>
              <span className="font-medium text-gray-800">{doc.name}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => updateDocument(index, 'updated')}
                className={`w-6 h-6 rounded border-2 flex items-center justify-center ${doc.updated
                  ? 'bg-violet-900 border-violet-900 text-white'
                  : 'border-gray-300'
                  }`}
              >
                {doc.updated && <Check size={14} />}
              </button>
              <span className="text-sm font-semibold text-violet-900">Updated</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="text-violet-900" size={20} />
          <span className="font-medium text-gray-800">Suggestion</span>
        </div>
        <p className="text-gray-700 text-sm">Establish a trust for your children</p>
      </div>
    </div>
  );

  const renderScenarios = () => (
    <div className="space-y-6">
      {/* Scenario Overview Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {scenarios.map((scenario) => (
          <div
            key={scenario.id}
            className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer transition-all ${selectedScenario.id === scenario.id
              ? 'ring-2 ring-violet-900 border-violet-900'
              : 'hover:shadow-xl'
              }`}
            onClick={() => setSelectedScenario(scenario)}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-800">{scenario.name}</h3>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${scenario.type === 'baseline' ? 'bg-blue-100 text-violet-900' :
                scenario.type === 'crisis' ? 'bg-red-100 text-red-800' :
                  'bg-green-100 text-green-800'
                }`}>
                {scenario.type.charAt(0).toUpperCase() + scenario.type.slice(1)}
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4">{scenario.description}</p>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Cost:</span>
                <span className="font-semibold">${scenario.outcomes.totalCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Care Hours/Week:</span>
                <span className="font-semibold">{scenario.parameters.careHours}h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Burnout Risk:</span>
                <span className={`font-semibold ${scenario.outcomes.caregiverBurnout > 60 ? 'text-red-600' :
                  scenario.outcomes.caregiverBurnout > 30 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                  {scenario.outcomes.caregiverBurnout}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Scenario Analysis */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            Detailed Analysis: {selectedScenario.name}
          </h3>
          <button
            onClick={() => setShowScenarioBuilder(true)}
            className="bg-violet-900 text-white px-4 py-2 rounded-lg hover:bg-blue-900 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Create Scenario
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Parameters */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4">Scenario Parameters</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Care Level:</span>
                <span className="font-medium capitalize">{selectedScenario.parameters.careLevel.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Health Decline Rate:</span>
                <span className="font-medium capitalize">{selectedScenario.parameters.healthDecline}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Living Arrangement:</span>
                <span className="font-medium capitalize">{selectedScenario.parameters.livingArrangement.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Care Hours/Week:</span>
                <span className="font-medium">{selectedScenario.parameters.careHours} hours</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Cost:</span>
                <span className="font-medium">${selectedScenario.parameters.monthlyCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time Horizon:</span>
                <span className="font-medium capitalize">{selectedScenario.parameters.timeHorizon.replace('_', ' ')}</span>
              </div>
            </div>
          </div>

          {/* Outcomes */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4">Projected Outcomes</h4>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Financial Strain</span>
                  <span className="font-medium">{selectedScenario.outcomes.financialStrain}%</span>
                </div>
                <ProgressBar value={selectedScenario.outcomes.financialStrain} color="red" />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Caregiver Burnout Risk</span>
                  <span className="font-medium">{selectedScenario.outcomes.caregiverBurnout}%</span>
                </div>
                <ProgressBar value={selectedScenario.outcomes.caregiverBurnout} color="yellow" />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Quality of Life</span>
                  <span className="font-medium">{selectedScenario.outcomes.qualityOfLife}%</span>
                </div>
                <ProgressBar value={selectedScenario.outcomes.qualityOfLife} color="green" />
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="text-2xl font-bold text-gray-800">
                  ${selectedScenario.outcomes.totalCost.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Projected Cost</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scenario Comparison */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-4">Scenario Comparison</h4>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 font-medium text-gray-700">Metric</th>
                  {scenarios.map(scenario => (
                    <th key={scenario.id} className="text-center py-3 font-medium text-gray-700">
                      {scenario.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-600">Total Cost</td>
                  {scenarios.map(scenario => (
                    <td key={scenario.id} className="text-center py-3 font-medium">
                      ${scenario.outcomes.totalCost.toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-600">Care Hours/Week</td>
                  {scenarios.map(scenario => (
                    <td key={scenario.id} className="text-center py-3 font-medium">
                      {scenario.parameters.careHours}h
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-600">Burnout Risk</td>
                  {scenarios.map(scenario => (
                    <td key={scenario.id} className="text-center py-3">
                      <span className={`font-medium ${scenario.outcomes.caregiverBurnout > 60 ? 'text-red-600' :
                        scenario.outcomes.caregiverBurnout > 30 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                        {scenario.outcomes.caregiverBurnout}%
                      </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-3 text-gray-600">Quality of Life</td>
                  {scenarios.map(scenario => (
                    <td key={scenario.id} className="text-center py-3">
                      <span className={`font-medium ${scenario.outcomes.qualityOfLife > 70 ? 'text-green-600' :
                        scenario.outcomes.qualityOfLife > 50 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                        {scenario.outcomes.qualityOfLife}%
                      </span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-200 p-6">
      {/* Full-screen Loading Spinner */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50">
          <div className="text-center">
            {/* Spinner */}
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-violet-900 rounded-full animate-spin mx-auto mb-4"></div>
              <div className="w-12 h-12 border-4 border-gray-100 border-t-violet-600 rounded-full animate-spin absolute top-2 left-1/2 transform -translate-x-1/2"></div>
            </div>
            {/* Loading Text */}
            <div className="text-violet-900 font-semibold text-lg mb-2">Loading Your Plan</div>
            <div className="text-gray-600 text-sm">Gathering and analyzing your information...</div>
            {/* Loading Dots Animation */}
            <div className="flex justify-center space-x-1 mt-4">
              <div className="w-2 h-2 bg-violet-900 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-violet-900 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-violet-900 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
              )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <div className="text-red-900 font-semibold text-lg mb-2">Unable to Load Your Plan</div>
            <div className="text-gray-600 text-sm mb-4">{error}</div>
            <button 
              onClick={() => {
                setError(null);
                setIsLoading(true);
                // Retry the API call
                window.location.reload();
              }}
              className="bg-violet-900 text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-violet-900">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="h-9 bg-gray-300 rounded-lg w-80"></div>
              </div>
            ) : (
              planData?.assistant_title || 'Interactive Care Plan'
            )}
          </h1>
          <div className="flex gap-4">
            <button
              className="p-2 bg-violet-900 rounded-lg shadow hover:shadow-md hover:bg-blue-900 transition-shadow group"
              title="Registered Consumers Only"
            >
              <Calendar size={20} className="text-white group-hover:hidden" />
              <CalendarOff size={20} className="text-white hidden group-hover:block" />
            </button>
            <button className="p-2 bg-violet-900 rounded-lg shadow hover:shadow-md hover:bg-blue-900 transition-shadow group relative"
              title="Registered Consumers Only"
            >
              <Settings size={20} className="text-white" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-6 h-0.5 bg-white transform rotate-45"></div>
              </div>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          <TabButton
            id="overview"
            label="Overview"
            icon={Home}
            active={activeTab === 'overview'}
            onClick={setActiveTab}
          />
          <TabButton
            id="scenarios"
            label="Scenarios"
            icon={TrendingUp}
            active={activeTab === 'scenarios'}
            onClick={setActiveTab}
          />
          <TabButton
            id="schedule"
            label="Schedule"
            icon={Calendar}
            active={activeTab === 'schedule'}
            onClick={setActiveTab}
          />
          <TabButton
            id="documents"
            label="Documents"
            icon={FileText}
            active={activeTab === 'documents'}
            onClick={setActiveTab}
          />
          <TabButton
            id="health"
            label="Health"
            icon={Activity}
            active={activeTab === 'health'}
            onClick={setActiveTab}
          />
          <TabButton
            id="finance"
            label="Finance"
            icon={DollarSign}
            active={activeTab === 'finance'}
            onClick={setActiveTab}
          />
        </div>

        {/* Main Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'scenarios' && (
          <SubscriptionOverlay>
            {renderScenarios()}
          </SubscriptionOverlay>
        )}
        {activeTab === 'schedule' && (
          <SubscriptionOverlay>
            {renderSchedule()}
          </SubscriptionOverlay>
        )}
        {activeTab === 'documents' && (
          <SubscriptionOverlay>
            {renderDocuments()}
          </SubscriptionOverlay>
        )}
        {activeTab === 'health' && (
          <SubscriptionOverlay>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Health Tracking</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medication Compliance
                    </label>
                    <ProgressBar value={healthData.medicationCompliance} color="green" />
                    <div className="text-right text-sm text-gray-600 mt-1">
                      {healthData.medicationCompliance}%
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caregiver Burnout Level
                    </label>
                    <ProgressBar value={healthData.burnoutLevel} color="yellow" />
                    <div className="text-right text-sm text-gray-600 mt-1">
                      {healthData.burnoutLevel}%
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Emergency Contacts</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-stone-200 rounded-lg">
                    <Phone size={18} className="text-gray-600" />
                    <div>
                      <div className="font-medium">Dr. Smith (Primary Care)</div>
                      <div className="text-sm text-gray-600">(555) 123-4567</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-stone-200 rounded-lg">
                    <Phone size={18} className="text-gray-600" />
                    <div>
                      <div className="font-medium">Emergency Services</div>
                      <div className="text-sm text-gray-600">911</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SubscriptionOverlay>
        )}
        {activeTab === 'finance' && (
          <SubscriptionOverlay>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Financial Planning</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-4">Monthly Budget Allocation</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Care Costs</span>
                      <span className="font-semibold">${(financialData.budgetBreakdown.careCosts * 50).toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Children's Expenses</span>
                      <span className="font-semibold">${(financialData.budgetBreakdown.childrenExpenses * 50).toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Other Expenses</span>
                      <span className="font-semibold">${(financialData.budgetBreakdown.other * 50).toFixed(0)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-4">Savings Goals</h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span>Emergency Fund</span>
                        <span>75%</span>
                      </div>
                      <ProgressBar value={75} color="green" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span>College Fund</span>
                        <span>45%</span>
                      </div>
                      <ProgressBar value={45} color="blue" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SubscriptionOverlay>
        )}

        {/* Scenario Builder Modal */}
        {showScenarioBuilder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-screen overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800">Create New Scenario</h3>
                  <button
                    onClick={() => setShowScenarioBuilder(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(scenarioParameters).map(([key, param]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {param.label}
                      </label>
                      <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-900 focus:border-transparent">
                        {param.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-4">Parameter Descriptions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <h5 className="font-medium text-gray-700">Care Level Impact:</h5>
                      <ul className="text-gray-600 space-y-1">
                        <li>• <strong>Minimal:</strong> Independent living with occasional check-ins</li>
                        <li>• <strong>Moderate:</strong> Daily assistance with ADLs, medication management</li>
                        <li>• <strong>Supported:</strong> Professional aide services, regular monitoring</li>
                        <li>• <strong>Intensive:</strong> 24/7 supervision, medical equipment</li>
                        <li>• <strong>Memory Care:</strong> Specialized dementia/Alzheimer's care</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h5 className="font-medium text-gray-700">Health Decline Factors:</h5>
                      <ul className="text-gray-600 space-y-1">
                        <li>• <strong>Stable:</strong> No anticipated decline</li>
                        <li>• <strong>Slow:</strong> Age-related changes over many years</li>
                        <li>• <strong>Gradual:</strong> Chronic conditions progressing steadily</li>
                        <li>• <strong>Rapid:</strong> Acute conditions or multiple health issues</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <button className="flex-1 bg-violet-900 text-white py-3 px-4 rounded-lg hover:bg-blue-900 transition-colors">
                    Calculate Scenario
                  </button>
                  <button
                    onClick={() => setShowScenarioBuilder(false)}
                    className="flex-1 bg-transparent border border-gray-700 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Member Modal */}
        {showAddMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Add Family Member</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Name"
                  value={newMember.name}
                  onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-900 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Age"
                  value={newMember.age}
                  onChange={(e) => setNewMember(prev => ({ ...prev, age: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-900 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Role (e.g., Father, Mother, Child)"
                  value={newMember.role}
                  onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-900 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={addFamilyMember}
                  className="flex-1 bg-violet-900 text-white py-2 px-4 rounded-lg hover:bg-blue-900 transition-colors"
                >
                  Add Member
                </button>
                <button
                  onClick={() => {
                    setShowAddMember(false);
                    setNewMember({ name: '', age: '', role: '' });
                  }}
                  className="flex-1 bg-transparent border border-gray-700 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaregivingDashboard;